import { Button, Drawer, Portal, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type ApiError, type IndicatorUpdate, IndicatorsService } from '@/client';
import { IndicatorDetails } from '@/components/indicators/index/drawer/IndicatorDetails';

import EditIndicatorDrawer from './EditIndicatorDrawer';

type IndicatorDrawerProps = {
  indicatorId?: string;
  isOpen: boolean;
  editDisabled?: boolean;
  setIsOpen: (state: boolean) => void;
  refetch?: () => void;
};

const IndicatorDrawer = ({ indicatorId, isOpen, editDisabled = false, setIsOpen, refetch }: IndicatorDrawerProps) => {
  const { t } = useTranslation();
  const { slug } = useParams({ strict: false });
  const [editMode, setEditMode] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const [_indicatorId, setIndicatorId] = useState<string | undefined>(indicatorId ?? '');

  useEffect(() => {
    setIndicatorId(indicatorId ?? undefined);
  }, [indicatorId, isOpen]);

  const mutation = useMutation({
    mutationFn: (data: IndicatorUpdate) => {
      return IndicatorsService.updateIndicator({
        companySlug: slug,
        requestBody: data,
        id: indicatorData?.id ?? '',
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['indicatorDetails', { slug, id: _indicatorId }],
      });
      queryClient.invalidateQueries({
        queryKey: ['indicators'],
      });
      setIsOpen(false);
      refetch?.();
    },
    onError: (err: ApiError) => console.error(err),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['indicators'] });
      queryClient.invalidateQueries({
        queryKey: ['indicatorDetails', { slug, id: indicatorData?.id }],
      });
    },
  });

  const { data: indicatorData } = useQuery({
    queryKey: ['indicatorDetails', { slug, id: _indicatorId }],
    queryFn: async () => {
      if (!_indicatorId) throw new Error('Indicator ID is required');
      return IndicatorsService.readIndicator({
        id: _indicatorId,
        companySlug: slug,
      });
    },
    enabled: !!_indicatorId && isOpen,
  });

  useEffect(() => {
    if (!isOpen) setEditMode(false);
  }, [isOpen]);

  return (
    <Drawer.Root open={isOpen} onOpenChange={(e) => setIsOpen(e.open)} size={{ md: 'lg', base: 'md' }} placement="end">
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content
            rounded="md"
            position="absolute"
            margin="{spacing.4}"
            h="calc(100% - {spacing.4} * 2)"
            right={0}
            top={0}
          >
            <Drawer.Header display="flex" justifyContent="space-between" alignItems="center">
              {editMode ? (
                <Text textStyle="xl" fontWeight="bold">
                  {t('indicator.drawer.edit.title')}
                </Text>
              ) : (
                <Text textStyle="xl" fontWeight="bold">
                  {t('indicator.drawer.details.title')}
                </Text>
              )}

              <Drawer.CloseTrigger asChild>
                <Button variant="ghost">✖</Button>
              </Drawer.CloseTrigger>
            </Drawer.Header>
            {indicatorData ? (
              editMode ? (
                <EditIndicatorDrawer
                  isOpen={isOpen}
                  setEditMode={setEditMode}
                  indicatorData={indicatorData}
                  mutation={mutation}
                />
              ) : (
                <IndicatorDetails
                  indicatorData={indicatorData}
                  editMode={editMode}
                  setEditMode={setEditMode}
                  switchToIndicator={(__indicatorId) => {
                    setIndicatorId(__indicatorId);
                  }}
                  onClose={() => {
                    setIsOpen(false);
                    refetch?.();
                  }}
                  editDisabled={editDisabled}
                />
              )
            ) : (
              <Spinner />
            )}
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};

export default IndicatorDrawer;
